import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { cloneDeep, isEmpty, toLower } from 'lodash'
import pluralize from 'pluralize'
import { useQuery } from '@/utils/hooks/useQuery'
import { submitMassPlaybookUpdate } from '@/reducers/playbooks/playbooks.actions'
import { normalizeDPValues } from '@/reducers/playbooks/playbook.helpers'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { ModalActions } from '@/components/modals/ModalActions'
import {
  accessorSectionMap,
  massPlaybookUpdateProgressStatus,
  playbookTitlesSectionMap,
} from '@/views/Playbooks/playbook.helpers'
import { Button } from '@/components/buttons/Button'

import { MassPlaybookUpdateWarningBanner } from '../modules/shared/MassPlaybookUpdateComponents'

export const PlaybookFormActions = ({
  isDisabled,
  dirty,
  handleCancel,
  values = {},
  useCustomDirtyValidation = false,
}) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { id: config_id, section, entryId: content_id } = useParams()
  const query = useQuery()
  const categoryId = query.get('category_id')

  const { playbook, listIDsInDB = {} } = useSelector((state) => state.playbook)
  const { massPlaybookUpdate, playbooks } = useSelector((state) => state.playbooks)

  const { isEnabled, progress, matches, selection, categorySelection, newPlaybookVersion } =
    massPlaybookUpdate || {}
  const massPlabookUpdateOn = isEnabled && !isEmpty(selection)
  const accessor = accessorSectionMap[section]
  const isExistingItem = listIDsInDB[accessor]?.includes(content_id)

  const [showConfirmPublish, setShowConfirmPublish] = useState(false)
  const initialValuesRef = useRef(null)
  const [hasRealChanges, setHasRealChanges] = useState(false)
  const useDirtyOrHasRealChanges = useCustomDirtyValidation ? hasRealChanges : dirty

  useEffect(() => {
    if (useCustomDirtyValidation && !initialValuesRef.current) {
      const { display_duration_unit, ...initialValues } = values

      const normalizedValues = normalizeDPValues(initialValues, true)
      initialValuesRef.current = normalizedValues
    }
  }, [useCustomDirtyValidation])

  useEffect(() => {
    if (useCustomDirtyValidation && initialValuesRef.current) {
      const normalizedCurrentValues = normalizeDPValues(values)
      const { display_duration_unit, ...currentValues } = normalizedCurrentValues

      const hasChanges = JSON.stringify(currentValues) !== JSON.stringify(initialValuesRef.current)

      setHasRealChanges(hasChanges)
    }
  }, [useCustomDirtyValidation, values])

  const handleClose = () => {
    if (newPlaybookVersion) {
      history.push(`/playbooks/${newPlaybookVersion}/${section}`)
    } else {
      handleCancel()
    }
  }

  // Adding current playbook in case it's drafted
  const orgPlaybooks = [cloneDeep(playbook), ...playbooks]

  const handleSubmitMassPlaybookUpdate = () =>
    dispatch(
      submitMassPlaybookUpdate(
        orgPlaybooks,
        section,
        matches,
        selection,
        categorySelection,
        config_id,
        content_id,
        categoryId,
        values,
        isExistingItem,
        setShowConfirmPublish
      )
    )

  return (
    <>
      <div className="playbook-form-actions">
        <div className="flex-align-center small-gap" style={{ marginLeft: 'auto' }}>
          <Button
            data-testid="playbook-cancel-button"
            onClick={handleCancel}
            disabled={progress !== massPlaybookUpdateProgressStatus.INITIAL}
          >
            Cancel
          </Button>
          {massPlabookUpdateOn ? (
            progress === massPlaybookUpdateProgressStatus.INITIAL ? (
              <Button
                brand
                data-testid="playbook-publish-button"
                disabled={isDisabled}
                onClick={() => setShowConfirmPublish(true)}
              >
                Bulk Update
              </Button>
            ) : (
              <Button
                data-testid="playbook-publish-close-button"
                disabled={progress === massPlaybookUpdateProgressStatus.IN_PROGRESS}
                onClick={handleClose}
              >
                Close
              </Button>
            )
          ) : (
            <Button brand type="submit" data-testid="playbook-submit-button" disabled={isDisabled}>
              Update
            </Button>
          )}
        </div>
        {useDirtyOrHasRealChanges && progress !== massPlaybookUpdateProgressStatus.DONE && (
          <ErrorMessage content="You have unsaved changes on this page!" />
        )}
      </div>
      {showConfirmPublish && (
        <BasicModal
          title="Bulk Edit Playbooks"
          onClose={() => setShowConfirmPublish(false)}
          show={showConfirmPublish}
          size="tiny"
        >
          <MassPlaybookUpdateWarningBanner />
          <div data-testid="confirm-delete-modal">
            {`Publish current and ${selection.length} selected ${toLower(pluralize(playbookTitlesSectionMap[section], selection.length))}?`}
          </div>
          <ModalActions
            onClose={() => setShowConfirmPublish(false)}
            onSubmit={handleSubmitMassPlaybookUpdate}
            buttonLabel="Publish"
          />
        </BasicModal>
      )}
    </>
  )
}
